import { createSlice } from "@reduxjs/toolkit";
import { PURGE } from "redux-persist";

import {
  IEntityRepeatModel,
  TransactionRepeatEnumModel,
} from "../../../types/types";
import { ITransactionInfoModel } from "../../../types/types";
import {
  clientRepeatsLoading,
  clientRepeatsLoaded,
  clientRepeatsError,
  soiReferrersLoading,
  soiReferrersLoaded,
  soiReferrersError,
  vendorReferrersLoading,
  vendorReferrersLoaded,
  vendorReferrersError,
  agentReferrersLoading,
  agentReferrersLoaded,
  agentReferrersError,
  selectedTransactionsLoading,
  selectedTransactionsLoaded,
  selectedTransactionsError,
  selectedTransactionsCleared,
  agentReferreredLoaded,
  agentReferreredLoading,
  agentReferreredError,
  combinedRepeatsAndReferrersLoading,
  combinedRepeatsAndReferrersLoaded,
  combinedRepeatsAndReferrersError,
} from "./repeatsAndReferrersStateActions";

interface RepeatsAndReferrersState {
  clientRepeats: {
    data: IEntityRepeatModel[];
    loading: boolean;
    error: string | null;
  };
  soiReferrers: {
    data: IEntityRepeatModel[];
    loading: boolean;
    error: string | null;
  };
  vendorReferrers: {
    data: IEntityRepeatModel[];
    loading: boolean;
    error: string | null;
  };
  agentReferrers: {
    data: IEntityRepeatModel[];
    loading: boolean;
    error: string | null;
  };
  selectedTransactions: {
    data: ITransactionInfoModel[];
    loading: boolean;
    error: string | null;
  };
  agentsReferrered: {
    data: IEntityRepeatModel[];
    loading: boolean;
    error: string | null;
  };
  combinedRepeatsAndReferrers: {
    data: IEntityRepeatModel[];
    loading: boolean;
    error: string | null;
  };
}

const initialState: RepeatsAndReferrersState = {
  clientRepeats: {
    data: [],
    loading: false,
    error: null,
  },
  soiReferrers: {
    data: [],
    loading: false,
    error: null,
  },
  vendorReferrers: {
    data: [],
    loading: false,
    error: null,
  },
  agentReferrers: {
    data: [],
    loading: false,
    error: null,
  },
  selectedTransactions: {
    data: [],
    loading: false,
    error: null,
  },
  agentsReferrered: {
    data: [],
    loading: false,
    error: null,
  },
  combinedRepeatsAndReferrers: {
    data: [],
    loading: false,
    error: null,
  },
};

const repeatsAndReferrersSlice = createSlice({
  name: "repeatsAndReferrers",
  initialState,
  reducers: {
    clearSelectedTransactions: (state) => {
      state.selectedTransactions.data = [];
      state.selectedTransactions.loading = false;
      state.selectedTransactions.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => {
      return initialState;
    });

    // Client Repeats
    builder
      .addCase(clientRepeatsLoading, (state) => {
        state.clientRepeats.loading = true;
        state.clientRepeats.error = null;
      })
      .addCase(clientRepeatsLoaded, (state, action) => {
        state.clientRepeats.loading = false;
        state.clientRepeats.data = action.payload;
      })
      .addCase(clientRepeatsError, (state, action) => {
        state.clientRepeats.loading = false;
        state.clientRepeats.error = action.payload;
      });

    // SOI Referrers
    builder
      .addCase(soiReferrersLoading, (state) => {
        state.soiReferrers.loading = true;
        state.soiReferrers.error = null;
      })
      .addCase(soiReferrersLoaded, (state, action) => {
        state.soiReferrers.loading = false;
        state.soiReferrers.data = action.payload;
      })
      .addCase(soiReferrersError, (state, action) => {
        state.soiReferrers.loading = false;
        state.soiReferrers.error = action.payload;
      });

    // Vendor Referrers
    builder
      .addCase(vendorReferrersLoading, (state) => {
        state.vendorReferrers.loading = true;
        state.vendorReferrers.error = null;
      })
      .addCase(vendorReferrersLoaded, (state, action) => {
        state.vendorReferrers.loading = false;
        state.vendorReferrers.data = action.payload;
      })
      .addCase(vendorReferrersError, (state, action) => {
        state.vendorReferrers.loading = false;
        state.vendorReferrers.error = action.payload;
      });

    // Agent Referrers
    builder
      .addCase(agentReferrersLoading, (state) => {
        state.agentReferrers.loading = true;
        state.agentReferrers.error = null;
      })
      .addCase(agentReferrersLoaded, (state, action) => {
        state.agentReferrers.loading = false;
        state.agentReferrers.data = action.payload;
      })
      .addCase(agentReferrersError, (state, action) => {
        state.agentReferrers.loading = false;
        state.agentReferrers.error = action.payload;
      });

    // Agents Referred
    builder
      .addCase(agentReferreredLoading, (state) => {
        state.agentsReferrered.loading = true;
        state.agentsReferrered.error = null;
      })
      .addCase(agentReferreredLoaded, (state, action) => {
        state.agentsReferrered.loading = false;
        state.agentsReferrered.data = action.payload;
      })
      .addCase(agentReferreredError, (state, action) => {
        state.agentsReferrered.loading = false;
        state.agentsReferrered.error = action.payload;
      });

    // Combined Repeats and Referrers
    builder
      .addCase(combinedRepeatsAndReferrersLoading, (state) => {
        state.combinedRepeatsAndReferrers.loading = true;
        state.combinedRepeatsAndReferrers.error = null;
      })
      .addCase(combinedRepeatsAndReferrersLoaded, (state, action) => {
        state.combinedRepeatsAndReferrers.loading = false;
        state.combinedRepeatsAndReferrers.data = action.payload;
      })
      .addCase(combinedRepeatsAndReferrersError, (state, action) => {
        state.combinedRepeatsAndReferrers.loading = false;
        state.combinedRepeatsAndReferrers.error = action.payload;
      });

    // Selected Transactions
    builder
      .addCase(selectedTransactionsLoading, (state) => {
        state.selectedTransactions.loading = true;
        state.selectedTransactions.error = null;
      })
      .addCase(selectedTransactionsLoaded, (state, action) => {
        state.selectedTransactions.loading = false;
        state.selectedTransactions.data = action.payload;
      })
      .addCase(selectedTransactionsError, (state, action) => {
        state.selectedTransactions.loading = false;
        state.selectedTransactions.error = action.payload;
      })
      .addCase(selectedTransactionsCleared, (state) => {
        state.selectedTransactions.data = [];
        state.selectedTransactions.loading = false;
        state.selectedTransactions.error = null;
      });
  },
});

export const { clearSelectedTransactions } = repeatsAndReferrersSlice.actions;
export default repeatsAndReferrersSlice.reducer;
